/**
 * Mission 24's crash site: one of OUR reconnaissance aircraft, down and burnt out. It came in from
 * the north-east, so the scar and the debris trail run that way and the biggest piece - the forward
 * fuselage, with the cockpit the recorder is behind - lies at the south-west end of it. The player's
 * band on the wing and the fin says whose it was; the sim treats the whole 8 x 9 m as solid.
 */

import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';
import { tubeZ } from './parts';
import { prism } from './shapes';

const TAU = Math.PI * 2;

type Outline = ReadonlyArray<readonly [number, number]>;
/** One wing, [x, z] about its root: shared by the wing still on and the one thrown clear. */
const WING: Outline = [[0, 0.8], [-2.6, 0.3], [-2.6, -0.5], [0, -0.8]];

function skin(p: Palette): BoxColors {
  return { all: p.color('metal.mid'), top: p.color('metal.hi'), bottom: p.color('ink.1') };
}

export const crashSite: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const burnt = { all: p.color('ink.1'), top: p.color('ink.1') };
  // The scar: overlapping burnt patches, thick enough to stay above rough ground, and two ploughed berms.
  mb.box(3.0, 0.12, 7.6, burnt, place(0.5, 0, -0.4, -0.4));
  mb.box(3.4, 0.1, 3.6, burnt, place(-1.0, 0, 1.6, 0.3));
  mb.box(1.8, 0.14, 2.2, { all: p.color('ink.0'), top: p.color('ink.0') }, place(-0.7, 0, 1.4, 0.3));
  for (const sx of [-1, 1]) {
    const berm = { all: p.color('dirt.lo'), top: p.color('dirt.mid') };
    mb.box(0.55, 0.32, 2.6, berm, place(2.0 + sx * 0.95, 0, -2.9 - sx * 0.35, -0.4));
  }

  // Forward fuselage, listing, its broken end open to the north-east; cockpit glazing; stub of a nose.
  mb.with(place(-0.8, 0, 1.5, 0.3, 1, 0, 0.14), () => {
    mb.box(1.5, 1.45, 3.6, { ...skin(p), back: p.color('ink.0') });
    mb.box(1.0, 0.45, 1.3, p.color('ink.1'), place(0, 1.45, 0.9));
    mb.box(1.1, 0.9, 0.5, p.color('metal.lo'), place(0, 0.2, 2.0));
    mb.box(0.9, 0.06, 1.5, p.color('playerAccent.0'), place(0, 1.45, -0.9));
    // The port wing is still on, its tip dug in.
    mb.with(place(-0.75, 0.75, -0.2, 0, 1, 0, 0.05), () => {
      prism(mb, WING, 0, 0.16, { top: p.color('metal.hi'), side: p.color('metal.lo'), bottom: p.color('ink.1') });
      mb.box(0.6, 0.05, 1.0, p.color('playerAccent.0'), place(-1.9, 0.15, -0.05));
    });
  });
  // Rear fuselage thrown round to the north-east, the fin still standing on it with our flash at its tip.
  mb.with(place(1.9, 0, -2.4, -0.7, 1, 0, -0.1), () => {
    mb.box(1.2, 1.1, 2.9, { ...skin(p), front: p.color('ink.0') });
    mb.box(0.14, 1.3, 1.2, { all: p.color('metal.mid'), top: p.color('metal.hi') }, place(0, 1.1, -0.7));
    mb.box(0.18, 0.4, 1.24, p.color('playerAccent.0'), place(0, 2.0, -0.7));
    mb.box(2.7, 0.1, 0.9, { all: p.color('metal.mid'), top: p.color('metal.hi') }, place(0, 0.95, -1.0));
    // Thrown clear of the fire, so the one part of her the snow has begun to cover.
    if (p.biome === 'arctic') mb.box(0.8, 0.08, 1.5, p.color('ground.3'), place(0.05, 1.1, 0.6));
  });
  // The starboard wing torn off and burnt; an engine that rolled clear, one blade of its propeller left.
  mb.with(place(2.6, 0.1, 2.4, 2.4, 1, 0.05), () => {
    prism(mb, WING, 0, 0.16, { top: p.color('ink.1'), side: p.color('ink.0') });
  });
  mb.with(place(2.3, 0.5, 0.6, 0.9), () => {
    tubeZ(mb, 0, 0, -0.7, 0.7, 0.48, 0.4, 8, p.color('metal.lo'), p.color('ink.0'));
    mb.box(0.16, 1.5, 0.06, p.color('ink.1'), place(0, -0.3, 0.76, 0, 1, 0, 0.5));
  });
  // Panels scattered down the trail.
  for (let i = 0; i < 8; i++) {
    const tone = p.color(rng.chance(0.4) ? 'ink.1' : rng.chance(0.5) ? 'metal.mid' : 'metal.hi');
    const x = rng.range(-3.3, 3.3);
    const at = place(x, 0.05, rng.range(-3.9, 3.9), rng.range(0, TAU), 1, rng.range(-0.25, 0.25), rng.range(-0.2, 0.2));
    mb.box(rng.range(0.5, 1.1), 0.07, rng.range(0.4, 0.9), tone, at);
  }
  return mb;
};
